import * as React from 'react'
import { LocaleSelectionSubMenu } from './LocaleSelector'
import { Link } from 'DVWebNodeUI'
import { NavLanguage, NavLink, StringMap } from 'DVWebNodeUtils';
import { ProfilesDropdown, ProfilesMenu } from '../../Shared/components/ProfilesMenu';
import { NavProfiles } from '../../Shared/transforms/Profiles';

interface LinkProps {
  text?: string,
  href?: string,
  rel?: string,
  isHighlighted?: boolean
}

export interface LocaleSelectionSubMenuProps {
  text: string,
  dropdownIndex: number,
  locales: NavLink[],
  languages: NavLanguage[]
}

interface AccountLinksProps {
  links: Array<any>,
  localeSelectionSubMenu: LocaleSelectionSubMenuProps,
  cerberus?: NavProfiles,
  strings?: StringMap
}

const AccountLinks = ({ links, localeSelectionSubMenu, cerberus, strings }: AccountLinksProps) => {
  const linkItems = links.slice()
  if (localeSelectionSubMenu) {
    // This is not ideal but we've been hardcoding dropdownIndex since ever .. would be great to get this in WPS at some point
    linkItems.splice(localeSelectionSubMenu.dropdownIndex, 0, localeSelectionSubMenu)
  }
  const listItems = linkItems.map(item => {
    if(item.link || item.locales) {
      return (<LocaleSelectionSubMenu locales={item.locales} removeIcon={false} languages={item.languages} languageToken={item.languageToken} text={item.text || ''} key={item.text} />)
    }
    return (
      <li key={item.text} role="menuitem">
        <Link {...item} key={item.text} />
      </li>
    )
  })
  if (cerberus) {
    return (
      <ul role="menu">
        <div className="pv-nav-dropl-column nav-profiles-menu">
          <span className="pv-accounts-cerberus-heading"> {strings['Profiles'] || 'Profiles'} </span>
          {cerberus && <ProfilesMenu {...cerberus} />}
        </div>
        <div className="pv-nav-dropl-column pv-nav-dropl-column-left">
          <span className="pv-accounts-cerberus-heading"> {strings['More'] || 'More'} </span>
          {listItems}
        </div>
      </ul>
    )
  }
  return (
    <ul role="menu">
      {listItems}
    </ul>
  )
}

type AnnotatedLinkProps = {
  link: LinkProps,
  pre?: string,
  post?: string
} & LinkProps

const AnnotatedLink = (props: AnnotatedLinkProps) => (
  <span>
    {props.pre && <div className="pv-nav-pre-text">{props.pre}</div>}
    <Link {...props} />
    {props.post && <div className="pv-nav-post-text">{props.post}</div>}
  </span>
)

/*
  YourAccountLink should be used only when there is only 1 link.
  We assume when there is only 1 link, that link is an annotated link.
 */

type YourAccountLinkProps = {
  link: AnnotatedLinkProps,
  dropdownExists: boolean
}

const YourAccountLink = ({link, dropdownExists}: YourAccountLinkProps) => {
  if (dropdownExists) {
    return (<span className="pv-nav-link-with-droplist"><AnnotatedLink {...link} /></span>)
  }

  return (<AnnotatedLink {...link} />)
}

/*
  For the dropdown scenerio, we only show the core link
  without pre or post annotations (i.e. non-annotated link).
 */
type YourAccountDropdownProps = {
  links: LinkProps[]
  localeSelectionSubMenu: LocaleSelectionSubMenuProps
  altText?: string
  cerberus?: NavProfiles | null
  vtr?: string
  strings?: StringMap
}

declare global {
  interface WindowCSM { ue: any; }
}

interface YourAccountDropdownState {
  checked: boolean
  triggered: boolean
}

class YourAccountDropdown extends React.Component<YourAccountDropdownProps, YourAccountDropdownState>{
  constructor(props: YourAccountDropdownProps) {
    super(props)
    this.state = {
      checked: false,
      triggered: false
    }
  }

  render() {
    const { links, localeSelectionSubMenu, altText, cerberus, strings } = this.props
    const accountsContainer = cerberus ?
    <div className='pv-nav-accounts-cerberus nav-profiles pv-nav-dropl' id="pv-nav-accounts-cerberus">
        <ProfilesDropdown {...cerberus} />
        <AccountLinks links={links} localeSelectionSubMenu={localeSelectionSubMenu} cerberus={cerberus} strings={strings} />
    </div>
    :
    <div className="pv-nav-accounts pv-nav-dropl" id="pv-nav-accounts">
      <input type="checkbox" autoComplete="off" name="pv-nav-accounts" id="pv-nav-accounts-menu-trigger" className="pv-nav-dropl__input avu-screen-reader-only" />
      <label htmlFor="pv-nav-accounts-menu-trigger" className="pv-nav-dropl__label">
        <span className="pv-nav-account-icon">{altText}</span>
      </label>
      <AccountLinks links={links} localeSelectionSubMenu={localeSelectionSubMenu} />
    </div>
    
    return (<>
    {accountsContainer}
  </>)
  }
}

type YourAccountProps = {
  links: Array<any>,
  localeSelectionSubMenu: LocaleSelectionSubMenuProps,
  altText?: string,
  vtr?: string,
  cerberus?: NavProfiles
  strings?: StringMap
}

export const YourAccount = ({links, localeSelectionSubMenu, altText, vtr, cerberus, strings}: YourAccountProps) => {
  if (links.length === 1) {
    return (
        <span className="pv-nav-accounts-single-link">
        <YourAccountLink link={links[0]} dropdownExists={Boolean(localeSelectionSubMenu)} />
        {localeSelectionSubMenu &&
          <YourAccountDropdown links={Array(links[0])}
            localeSelectionSubMenu={localeSelectionSubMenu} altText={altText} cerberus={cerberus} strings={strings} />}
      </span>      
    )
  }
  return (
    <YourAccountDropdown links={links}
      localeSelectionSubMenu={localeSelectionSubMenu} vtr={vtr} altText={altText} cerberus={cerberus} strings={strings} />
  )    
}

